// AssetDepreciationReportHTML.js
import { Button, TextField, FormControlLabel, Checkbox, InputAdornment, IconButton, styled, Box } from "@mui/material";
import React, { useEffect, useState, useRef, useMemo } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TableChartIcon from "@mui/icons-material/TableChart";
import ReportsService from "../../../Services/reports.service";
import AssetService from "../../../Services/assets.service";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import Draggable from 'react-draggable';
import CustomYearDatePicker from "../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker";
import CustomMonthDatePicker from "../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker";
import CancelIcon from '@mui/icons-material/Cancel';
import dayjs from 'dayjs';
import { beautifyNumber } from "../../../Utils/FormatNumber";
import {
    SOLD_DISPLAY_COLOR,
    buildEnrichedAssetLookup,
    resolveEnrichedAsset,
    isAssetSold,
    isAssetSoldForReportDisplay,
    formatAssetDescription,
} from "../../../Utils/assetSaleDisplayUtils";

const BoundsWrapper = styled("div")({
    position: "fixed",
    inset: 0,
    zIndex: 200,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});

const ModalOverlay = styled("div")({
    width: "min(96vw, 1300px)",
    height: "min(92vh, 900px)",
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
    border: "1px solid #304FFF",
    "& .report-body-scroll": {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
    },
});

const handleStyle = {
    cursor: "move",
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};

const DEFAULT_DEPRECIATION_SORT = { key: "purchaseDate", direction: "asc" };

const DEPRECIATION_SORT_TYPES = {
    asset: "string",
    purchaseDate: "date",
    originalPrice: "number",
    changeOfYear: "number",
    depreciableAssets: "number",
    depreciationPercentage: "number",
    claimedThisYear: "number",
    depreciationUntilNow: "number",
    depreciationThisYear: "number",
    totalDepreciation: "number",
    reducedCost: "number",
    description: "string",
    sellDate: "date",
    sellAmount: "number",
};

function isEmptySortValue(value) {
    return value == null || value === "";
}

function compareDepreciationSortValues(a, b, type, direction) {
    const aEmpty = isEmptySortValue(a);
    const bEmpty = isEmptySortValue(b);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    const dir = direction === "desc" ? -1 : 1;
    if (type === "number") {
        return dir * ((Number(a) || 0) - (Number(b) || 0));
    }
    if (type === "date") {
        const aTime = new Date(a).getTime();
        const bTime = new Date(b).getTime();
        const aInvalid = Number.isNaN(aTime);
        const bInvalid = Number.isNaN(bTime);
        if (aInvalid && bInvalid) return 0;
        if (aInvalid) return 1;
        if (bInvalid) return -1;
        return dir * (aTime - bTime);
    }
    return dir * String(a).localeCompare(String(b), "he", { numeric: true, sensitivity: "base" });
}

function depreciationSortValue(row, key, enrichedAsset) {
    const asset = row?.asset || {};
    const changeOfYear = row?.changeOfYear || 0;
    const isNewThisYear = changeOfYear !== 0;
    switch (key) {
        case "asset":
            return `${asset.assetNumber || ""} ${asset.name || ""}`.trim();
        case "purchaseDate":
            return asset.purchaseDate || asset.activationDate || null;
        case "originalPrice":
            return isNewThisYear ? null : (asset.originalPrice ?? null);
        case "changeOfYear":
            return changeOfYear !== 0 ? changeOfYear : null;
        case "depreciableAssets":
            return asset.originalPrice ?? null;
        case "depreciationPercentage":
            return row?.depreciationPercentage ?? null;
        case "claimedThisYear":
            return row?.claimedThisYear ?? null;
        case "depreciationUntilNow":
            return row?.depreciationUntilNow ?? null;
        case "depreciationThisYear":
            return row?.depreciationThisYear ?? null;
        case "totalDepreciation":
            return row?.totalDepreciation ?? null;
        case "reducedCost":
            return row?.reducedCost ?? null;
        case "description":
            return formatAssetDescription(
                enrichedAsset?.description,
                enrichedAsset?.sellDate,
                isAssetSold(enrichedAsset)
            );
        case "sellDate":
            return enrichedAsset?.sellDate || null;
        case "sellAmount":
            return enrichedAsset?.sellAmount ?? null;
        default:
            return null;
    }
}

function sortDepreciationAssets(assets, sortConfig, resolveEnriched) {
    const key = sortConfig?.key || DEFAULT_DEPRECIATION_SORT.key;
    const direction = sortConfig?.direction || DEFAULT_DEPRECIATION_SORT.direction;
    const type = DEPRECIATION_SORT_TYPES[key] || "string";
    return [...(assets || [])].sort((a, b) => {
        const enrichedA = resolveEnriched?.(a);
        const enrichedB = resolveEnriched?.(b);
        const primary = compareDepreciationSortValues(
            depreciationSortValue(a, key, enrichedA),
            depreciationSortValue(b, key, enrichedB),
            type,
            direction
        );
        if (primary !== 0) return primary;
        const byPurchaseDate = compareDepreciationSortValues(
            depreciationSortValue(a, "purchaseDate", enrichedA),
            depreciationSortValue(b, "purchaseDate", enrichedB),
            "date",
            "asc"
        );
        if (byPurchaseDate !== 0) return byPurchaseDate;
        return compareDepreciationSortValues(
            a?.asset?.assetNumber,
            b?.asset?.assetNumber,
            "string",
            "asc"
        );
    });
}

function depreciationHeaderCell(className, key, label, sortConfig, forPdf) {
    if (forPdf) {
        return `<th class="${className}">${label}</th>`;
    }
    const active = sortConfig?.key === key;
    const arrow = active
        ? `<span class="sort-ind">${sortConfig.direction === "desc" ? "▼" : "▲"}</span>`
        : "";
    return `<th class="${className} sortable${active ? " sorted" : ""}" onclick="window.dispatchEvent(new CustomEvent('depreciationColumnSort', { detail: '${key}' }))">${arrow}${label}</th>`;
}

export function generateDepreciationReportHtml(
    depreciationDetails,
    client,
    date,
    t,
    showEmptyGroups = false,
    forPdf = false,
    assetRelations = [],
    sortConfig = DEFAULT_DEPRECIATION_SORT
) {

    if (!depreciationDetails || !depreciationDetails.assetGroups) {
        return "<p>No depreciation data available</p>";
    }

    const reportDate = date ? new Date(date) : null;
    const reportYear = reportDate ? reportDate.getFullYear() : null;
    const isFullYear = reportDate ? (reportDate.getMonth() === 11 && reportDate.getDate() >= 30) : true;
    const formattedTaxYear = reportDate
        ? (isFullYear ? String(reportYear) : `${String(reportDate.getDate()).padStart(2,'0')}/${String(reportDate.getMonth()+1).padStart(2,'0')}/${reportYear}`)
        : "";

    const now = new Date();
    const currentTime = now.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", hour12: false });
    const currentDate = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
    const activeSort = sortConfig?.key ? sortConfig : DEFAULT_DEPRECIATION_SORT;

    // Initialize cumulative totals
    let totalOriginalPrice = 0;
    let totalChangeThisYear = 0;
    let totalDepreciableAssets = 0;
    let totalDepreciationUntilNow = 0;
    let totalTotalDepreciation = 0;
    let totalDepreciationThisYear = 0;
    let totalReducedCost = 0;

    const groupsToRender = showEmptyGroups
        ? depreciationDetails.assetGroups
        : depreciationDetails.assetGroups.filter((g) => g.assets && g.assets.length > 0);

    const allReportAssets = groupsToRender.flatMap((group) =>
        (group.assets || []).map((row) => row.asset).filter(Boolean)
    );
    const enrichedAssetLookup = buildEnrichedAssetLookup(allReportAssets, assetRelations);

    let content = `
    <!DOCTYPE html>
    <html lang="he">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
            * { box-sizing: border-box !important; }
            html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; direction: rtl; text-align: right; }

            .depreciation-report-container {
                width: 100%;
                max-width: 100%;
                margin: 0;
                padding: 12px 16px;
                font-size: 11px;
                line-height: 1.45;
                color: #111;
            }

            .pageStyle { margin-bottom: 12px; }
            .upper-name-div { display: flex; justify-content: space-between; align-items: flex-start; width: 100%; }
            .left-header-info { text-align: left; direction: ltr; }
            .right-header-info { text-align: right; }
            .current-datetime { font-size: 11px; color: #333; font-weight: 600; }
            .middle-details { margin: 10px 0; }
            .rt1 { margin: 0; font-weight: 700; font-size: 1em; }
            .det-small { margin: 2px 0; font-weight: 800; text-decoration: underline; font-size: 0.95em; }

            .header-info { width: 100%; display: flex; justify-content: space-between; margin-bottom: 14px; }
            .header-left, .header-right { display: flex; flex-direction: column; gap: 4px; font-size: 14px; font-weight: 600; }
            .report-header { margin-bottom: 12px; }
            .header-top { display: flex; justify-content: space-between; font-weight: 700; margin-bottom: 4px; font-size: 11px; }
            .main-title { font-size: 1.1em; margin: 6px 0 4px 0; font-weight: 700; }
            .main-title, .subtitle { text-align: center; color: #111; }

            .depreciation-report-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                font-size: 11px;
            }
            .depreciation-report-table thead { position: sticky; top: 0; z-index: 10; background: #f5f5f5; }
            .depreciation-report-table th,
            .depreciation-report-table td {
                white-space: normal;
                overflow: visible;
                text-overflow: unset;
                word-break: break-word;
            }
            .depreciation-report-table th {
                padding: 5px 10px;
                text-align: right;
                font-weight: 700;
                border-bottom: 2px solid #333;
                border-left: 1px solid #ddd;
                background: #f5f5f5;
            }
            .depreciation-report-table th:last-child { border-left: none; }
            .depreciation-report-table th.sortable { cursor: pointer; user-select: none; }
            .depreciation-report-table th.sortable:hover { background: #ececec; }
            .depreciation-report-table th.sorted { color: #304FFF; }
            .sort-ind { font-size: 9px; margin-inline-end: 4px; }
            .depreciation-report-table td {
                padding: 4px 10px;
                text-align: right;
                border-bottom: 1px solid #e0e0e0;
                border-left: 1px solid #eee;
                vertical-align: middle;
            }
            .depreciation-report-table td:last-child { border-left: none; }
            .depreciation-report-table .col-asset { width: 14%; }
            .depreciation-report-table .col-date { width: 9%; }
            .depreciation-report-table .col-num { width: 6%; }
            .depreciation-report-table .col-pct { width: 4%; }
            .depreciation-report-table .col-desc { width: 15%; }
            .depreciation-report-table .col-sell { width: 9%; }

            .group-header-row {
                font-weight: 800;
                font-size: 1.02em;
                padding: 6px 10px;
                background: #e8e8e8;
                border-bottom: 1px solid #ccc;
            }
            .group-header-row td { border-bottom: 1px solid #ccc; border-left: none; }

            .hoverTr { cursor: pointer; transition: background 0.15s ease; }
            .hoverTr:hover { background: #f9f9f9; }
            .sold-row { background: rgba(198, 40, 40, 0.10); color: ${SOLD_DISPLAY_COLOR}; font-weight: 600; }
            .hoverTr.sold-row:hover { background: rgba(198, 40, 40, 0.16); }
            .sold-row td { color: ${SOLD_DISPLAY_COLOR}; }

            .sum-row td { font-weight: 700; background: #f9f9f9; white-space: nowrap; padding: 6px 10px; }
            .sum-row.grand-total td { background: #e3e8f4; font-weight: 800; }

            .for-pdf .sold-row { background: transparent !important; color: inherit !important; }
            .for-pdf .sum-row td { background: transparent !important; }
            .for-pdf .sum-row.grand-total td { background: transparent !important; }
            .for-pdf .group-header-row { background: transparent !important; }
            .for-pdf .depreciation-report-table th,
            .for-pdf .depreciation-report-table td { border-left: none !important; }

            .for-pdf .depreciation-report-container { padding: 4px 6px !important; max-width: 100% !important; width: 100% !important; }
            .for-pdf .depreciation-report-table { width: 100% !important; }

            @page { size: A4 ${forPdf ? 'landscape' : 'portrait'}; margin: ${forPdf ? '6mm' : '12mm'}; }
            @media print {
                html, body { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
                .depreciation-report-container { max-width: 100% !important; width: 100% !important; padding: 4px 6px !important; font-size: 10px; }
                .depreciation-report-table { font-size: 10px; }
                .depreciation-report-table th, .depreciation-report-table td { padding: 4px 8px; }
                .depreciation-report-table thead { display: table-header-group; }
                .group-header-row { page-break-after: avoid; }
                .hoverTr:hover { background: inherit !important; }
            }
        </style>
    </head>
    <body class="${forPdf ? 'for-pdf' : ''}">
    <div class="depreciation-report-container">
        ${forPdf ? `
        <div class="pageStyle report-header-pdf">
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px; font-weight: 600;">
                <div style="display: flex; flex-direction: column; gap: 3px;">
                    <div>שם העסק : ${client?.name || ''}</div>
                    <div>פקיד שומה : ${client?.taxAssessor || ''}</div>
                    <div>שנת מס : ${formattedTaxYear}</div>
                </div>
                <div style="display: flex; flex-direction: column; gap: 3px; text-align: left; direction: ltr;">
                    <div>מספר זהות: ${client?.clientId || ''}</div>
                    <div>מספר תיק: ${client?.caseId || ''}</div>
                    <div style="font-size: 11px; color: #555;">תאריך : ${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}</div>
                </div>
            </div>
            <div class="header-top" style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; margin-bottom: 4px;">
                <span style="text-align: right; flex: 1;">מדינת ישראל/האוצר, אגף מס הכנסה ומס רכוש</span>
                <span style="text-align: left; flex-shrink: 0;">טופס 1342 (יא)</span>
            </div>
            <hr style="border: none; border-top: 1px solid #999; margin: 4px 0 8px 0;" />
            <h2 class="main-title" style="font-size: 16px; font-weight: 700; margin: 0 0 4px 0; text-align: center;">תקנות מס הכנסה (פחת) לשנה שנסתיימה ב־ ${isFullYear ? `31/12/${formattedTaxYear}` : formattedTaxYear}</h2>
            <p class="subtitle" style="font-size: 11px; margin: 0; text-align: center;">פרטים על נכסים שניתן לגבם פחת (חוק התיאומים סעיף 3, סעיף 21 וכו')</p>
        </div>
        ` : `
        <div class="header-info">
            <div class="header-left">
                <div>שם העסק : ${client?.name || ''}</div>
                <div>פקיד שומה : ${client?.taxAssessor || ''}</div>
                <div>שנת מס : ${formattedTaxYear}</div>
            </div>
            <div class="header-right">
                <div>מספר זהות: ${client?.clientId || ''}</div>
                <div>מספר תיק: ${client?.caseId || ''}</div>
            </div>
        </div>
        <div class="report-header">
            <div class="header-top">
                <span>מדינת ישראל/האוצר, אגף מס הכנסה ומס רכוש</span>
                <span>טופס 1342 (יא)</span>
            </div>
            <hr />
            <h2 class="main-title">תקנות מס הכנסה (פחת) לשנה שנסתיימה ב־ ${formattedTaxYear}</h2>
            <p class="subtitle">
                פרטים על נכסים שניתן לגבם פחת (חוק התיאומים סעיף 3, סעיף 21 וכו')
            </p>
        </div>
        `}

        <table class="depreciation-report-table">
            <colgroup>
                <col class="col-asset" />
                <col class="col-date" />
                <col class="col-num" />
                <col class="col-num" />
                <col class="col-num" />
                <col class="col-pct" />
                <col class="col-pct" />
                <col class="col-num" />
                <col class="col-num" />
                <col class="col-num" />
                <col class="col-num" />
                <col class="col-desc" />
                <col class="col-sell" />
                <col class="col-num" />
            </colgroup>
            <thead>
                <tr>
                    ${depreciationHeaderCell("col-asset", "asset", "רשימת הנכסים ותאורם", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-date", "purchaseDate", "תאריך רכישה/שינוי/הפעלה", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "originalPrice", "מחיר מקורי", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "changeOfYear", "שינוי השנה", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "depreciableAssets", "נכסים בני פחת", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-pct", "depreciationPercentage", "אחוז פחת%", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-pct", "claimedThisYear", "נתבע השנה%", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "depreciationUntilNow", "פחת שנצבר", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "depreciationThisYear", "פחת השנה", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "totalDepreciation", "סה״כ פחת", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "reducedCost", "עלות מופחתת", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-desc", "description", "הערות (ראה מפתח בדף אחרון)", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-sell", "sellDate", "תאריך מכירה", activeSort, forPdf)}
                    ${depreciationHeaderCell("col-num", "sellAmount", "סכום מכירה", activeSort, forPdf)}
                </tr>
            </thead>
            <tbody>
    `;

    // ============ RENDER GROUPS AND ASSETS ============
    const COLUMN_COUNT = 14;
    groupsToRender.forEach((group) => {
        const encodedDataGroup = window.btoa(unescape(encodeURIComponent(JSON.stringify(group))));

        const translatedGroupName = group.assetGroupName === "Unassigned Assets"
            ? t('UnassignedAssets')
            : group.assetGroupName;
        
        const groupLabel = (!group.assetGroupId || !group.assetGroupName)
            ? "פריטים ללא קבוצה"
            : `קבוצה: ${group.assetGroupId} - ${translatedGroupName}`;

        content += `
          <tr class="group-header-row">
            <td colspan="${COLUMN_COUNT}">${groupLabel}</td>
          </tr>
        `;

        // Initialize sums for the group
        let sumOriginalPrice = 0;
        let sumChangeThisYear = 0;
        let sumDepreciableAssets = 0;
        let sumDepreciationUntilNow = 0;
        let sumTotalDepreciation = 0;
        let sumDepreciationThisYear = 0;
        let sumReducedCost = 0;

        // RENDER EACH ASSET ROW (purchase date by default; column header can change the order)
        const sortedAssets = sortDepreciationAssets(group.assets, activeSort, (row) =>
            resolveEnrichedAsset(row?.asset, assetRelations, allReportAssets, enrichedAssetLookup)
        );
        sortedAssets.forEach((asset) => {
            const encodedData = window.btoa(unescape(encodeURIComponent(JSON.stringify(asset))));

            // Add to group sums for numeric columns
            const originalPrice = asset.asset.originalPrice || 0;
            const changeOfYear = asset.changeOfYear || 0;
            const isNewThisYear = changeOfYear !== 0;
            const displayOriginalPrice = isNewThisYear ? 0 : originalPrice;
            const depreciableAssets = asset.asset.originalPrice || 0;
            const depreciationUntilNow = asset.depreciationUntilNow || 0;
            const totalDepreciation = asset.totalDepreciation || 0;
            const depreciationThisYear = asset.depreciationThisYear || 0; // prorated when sold
            const reducedCost = asset.reducedCost || 0;

            const isSoldInReportYear =
                asset.asset?.sellDate &&
                reportYear != null &&
                new Date(asset.asset.sellDate).getFullYear() === reportYear;

            const enrichedAsset = resolveEnrichedAsset(
                asset.asset,
                assetRelations,
                allReportAssets,
                enrichedAssetLookup
            );
            const soldForDisplay = isAssetSoldForReportDisplay(
                asset.asset,
                assetRelations,
                reportYear,
                allReportAssets,
                enrichedAssetLookup
            );
            const soldDisplay = isAssetSold(enrichedAsset);
            const displayDescription = formatAssetDescription(
                enrichedAsset?.description,
                enrichedAsset?.sellDate,
                soldDisplay
            );
            const sellDateDisplay = enrichedAsset?.sellDate
                ? new Date(enrichedAsset.sellDate).toLocaleDateString("he-IL")
                : "—";
            const sellAmountDisplay =
                enrichedAsset?.sellAmount != null && enrichedAsset?.sellAmount !== ""
                    ? beautifyNumber(enrichedAsset.sellAmount, 0)
                    : "—";

            // Sold assets: exclude from original/depreciable/reduced sum; include prorated פחת השנה in calc
            const totalDepreciationForCalc = isSoldInReportYear
                ? (depreciationUntilNow || 0) + depreciationThisYear
                : totalDepreciation;

            if (!isSoldInReportYear) {
                sumOriginalPrice += displayOriginalPrice;
                sumDepreciableAssets += depreciableAssets;
                sumReducedCost += reducedCost;
                totalOriginalPrice += displayOriginalPrice;
                totalDepreciableAssets += depreciableAssets;
                totalReducedCost += reducedCost;
            }

            sumChangeThisYear += changeOfYear;
            sumDepreciationUntilNow += depreciationUntilNow;
            sumTotalDepreciation += totalDepreciationForCalc;
            sumDepreciationThisYear += depreciationThisYear;
            totalChangeThisYear += changeOfYear;
            totalDepreciationUntilNow += depreciationUntilNow;
            totalTotalDepreciation += totalDepreciationForCalc;
            totalDepreciationThisYear += depreciationThisYear;

            // Render the asset row (sold rows in red, except for PDF)
            const rowClass = !forPdf && soldForDisplay ? "hoverTr sold-row" : "hoverTr";
            const soldStyle = !forPdf && soldForDisplay ? ` style="color:${SOLD_DISPLAY_COLOR};"` : "";
            content += `
                <tr class="${rowClass}" 
                    onclick="window.parent.dispatchEvent(new CustomEvent('rowClick', { detail: '${encodedData}' }))">
                    <td${soldStyle}>${asset.asset.assetNumber || "—"} - ${asset.asset.name || "—"}</td>
                    <td${soldStyle}>${asset.asset.activationDate
                    ? new Date(asset.asset.activationDate).toLocaleDateString("he-IL")
                    : "—"
                }</td>
                    <td${soldStyle}>${isNewThisYear ? "—" : beautifyNumber(originalPrice, 0)}</td>
                    <td${soldStyle}>${changeOfYear !== 0 ? beautifyNumber(changeOfYear, 0) : "—"}</td>
                    <td${soldStyle}>${beautifyNumber(depreciableAssets, 0)}</td>
                    <td${soldStyle}>${beautifyNumber(asset.depreciationPercentage, 0)}%</td>
                    <td${soldStyle}>${beautifyNumber(asset.claimedThisYear, 0)}%</td>
                    <td${soldStyle}>${beautifyNumber(depreciationUntilNow, 0)}</td>
                    <td${soldStyle}>${beautifyNumber(depreciationThisYear, 0)}</td>
                    <td${soldStyle}>${beautifyNumber(totalDepreciation, 0)}</td>
                    <td${soldStyle}>${beautifyNumber(reducedCost, 0)}</td>
                    <td${soldStyle}>${displayDescription}</td>
                    <td${soldStyle}>${sellDateDisplay}</td>
                    <td${soldStyle}>${sellAmountDisplay}</td>
                </tr>
            `;
        });

        // ============ SUM ROW FOR THIS GROUP ============

        // We create a row that has a heavy top border, and columns aligned
        // to match the headings. Here, we put “סה״כ לקבוצה” in the first column, 
        // and fill numeric totals in the matching columns. 
        // For percentage columns, we’ll just put an empty cell.

        content += `
          <tr class="sum-row">
            <td colspan="2">סה"כ לקבוצה ${group.assetGroupName ? translatedGroupName : ''}</td>
            <td>${beautifyNumber(sumOriginalPrice, 0)}</td>
            <td>${beautifyNumber(sumChangeThisYear, 0)}</td>
            <td>${beautifyNumber(sumDepreciableAssets, 0)}</td>
            <td>-</td>
            <td>-</td>
            <td>${beautifyNumber(sumDepreciationUntilNow, 0)}</td>
            <td>${beautifyNumber(sumDepreciationThisYear, 0)}</td>
            <td>${beautifyNumber(sumTotalDepreciation, 0)}</td>
            <td>${beautifyNumber(sumReducedCost, 0)}</td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        `;
    });

    // Total row as last row inside tbody
    content += `
          <tr class="sum-row grand-total">
            <td colspan="2">סה״כ כללי</td>
            <td>${beautifyNumber(totalOriginalPrice, 0)}</td>
            <td>${beautifyNumber(totalChangeThisYear, 0)}</td>
            <td>${beautifyNumber(totalDepreciableAssets, 0)}</td>
            <td>-</td>
            <td>-</td>
            <td>${beautifyNumber(totalDepreciationUntilNow, 0)}</td>
            <td>${beautifyNumber(totalDepreciationThisYear, 0)}</td>
            <td>${beautifyNumber(totalTotalDepreciation, 0)}</td>
            <td>${beautifyNumber(totalReducedCost, 0)}</td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
            </tbody>
        </table>
    </div>
    </body>
    </html>`;

    return content;
}


/** This component uses the above function and displays the HTML **/
const AssetDepreciationReportHTML = ({
    open,
    depreciationContent,
    client,
    date,
    token,
    onRowClick,
    onClose,
    reportDate,
    handleEndDateChangeNew,
    setReportDate,
    onRefreshWithRange,
    assetRelations = [],
}) => {
    const [reportHtmlContent, setReportHtmlContent] = useState("");
    const [showEmptyGroups, setShowEmptyGroups] = useState(false);
    const [search, setSearch] = useState("");
    const [soldOnly, setSoldOnly] = useState(false);
    const [zoom, setZoom] = useState(1);
    const nodeRef = useRef(null);
    const boundsRef = useRef(null);
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [useRange, setUseRange] = useState(false);
    const [rangeEndMonth, setRangeEndMonth] = useState(() => new Date());
    const [sortConfig, setSortConfig] = useState(DEFAULT_DEPRECIATION_SORT);

    const reportYear = date ? new Date(date).getFullYear() : null;
    const componentReportDate = date ? new Date(date) : null;
    const componentIsFullYear = componentReportDate ? (componentReportDate.getMonth() === 11 && componentReportDate.getDate() >= 30) : true;
    const formattedTaxYear = componentReportDate
        ? (componentIsFullYear ? String(reportYear) : `${String(componentReportDate.getDate()).padStart(2,'0')}-${String(componentReportDate.getMonth()+1).padStart(2,'0')}-${reportYear}`)
        : '';

    const totalAssetCount = useMemo(() => {
        if (!depreciationContent?.assetGroups) return 0;
        return depreciationContent.assetGroups.reduce((sum, g) => sum + (g.assets?.length || 0), 0);
    }, [depreciationContent]);

    const filteredDepreciationContent = useMemo(() => {
        if (!depreciationContent?.assetGroups) return null;
        const searchLower = (search || "").trim().toLowerCase();
        const filteredGroups = depreciationContent.assetGroups
            .map((group) => {
                const translatedGroupName = group.assetGroupName === "Unassigned Assets"
                    ? t("UnassignedAssets")
                    : group.assetGroupName;
                let filteredAssets = (group.assets || []).filter((asset) => {
                    if (soldOnly && reportYear != null) {
                        const pool = (depreciationContent.assetGroups || []).flatMap((g) =>
                            (g.assets || []).map((row) => row.asset).filter(Boolean)
                        );
                        if (!isAssetSoldForReportDisplay(asset.asset, assetRelations, reportYear, pool)) {
                            return false;
                        }
                    }
                    if (searchLower) {
                        const num = (asset.asset?.assetNumber || "").toString();
                        const name = asset.asset?.name || "";
                        const desc = asset.asset?.description || "";
                        const grp = translatedGroupName || "";
                        const haystack = `${num} ${name} ${desc} ${grp}`.toLowerCase();
                        if (!haystack.includes(searchLower)) return false;
                    }
                    return true;
                });
                return { ...group, assets: filteredAssets };
            })
            .filter((group) => showEmptyGroups || (group.assets && group.assets.length > 0));
        return { ...depreciationContent, assetGroups: filteredGroups };
    }, [depreciationContent, search, soldOnly, showEmptyGroups, reportYear, t, assetRelations]);

    const filteredAssetCount = useMemo(() => {
        if (!filteredDepreciationContent?.assetGroups) return 0;
        return filteredDepreciationContent.assetGroups.reduce((sum, g) => sum + (g.assets?.length || 0), 0);
    }, [filteredDepreciationContent]);

    const handleResetFilters = () => {
        setSearch("");
        setSoldOnly(false);
    };
    const hasActiveFilters = !!(search.trim() || soldOnly);

    useEffect(() => {
        if (filteredDepreciationContent) {
            setReportHtmlContent(
                generateDepreciationReportHtml(
                    filteredDepreciationContent,
                    client,
                    date,
                    t,
                    showEmptyGroups,
                    false,
                    assetRelations,
                    sortConfig
                )
            );
        } else {
            setReportHtmlContent("<p>No depreciation data available</p>");
        }
    }, [filteredDepreciationContent, date, client, showEmptyGroups, assetRelations, t, sortConfig]);

    useEffect(() => {
        if (open) {
            setDragPosition({ x: 0, y: 0 });
            setSortConfig(DEFAULT_DEPRECIATION_SORT);
        }
    }, [open]);

    useEffect(() => {
        const handleColumnSort = (event) => {
            const key = event.detail;
            if (!key || !DEPRECIATION_SORT_TYPES[key]) return;
            setSortConfig((prev) => (
                prev.key === key
                    ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                    : { key, direction: "asc" }
            ));
        };
        window.addEventListener("depreciationColumnSort", handleColumnSort);
        return () => window.removeEventListener("depreciationColumnSort", handleColumnSort);
    }, []);

    //for handling a row click
    useEffect(() => {
        const handleRowClick = (event) => {
            try {
                // event.detail should be base64-encoded JSON
                const encodedData = event.detail;
                const decoded = atob(encodedData);   // decode base64
                const parsed = JSON.parse(decoded);  // parse JSON

                // If it's a group row, maybe there's an 'assetGroupId'
                // If it's an asset row, maybe there's 'asset.id'
                // Adjust these to your data structure
                if (parsed.asset) {
                    const Id = parsed.asset.id
                    onRowClick(Id, 'asset');
                } else if (parsed.assetGroupId) {
                    onRowClick(parsed.assetGroupId, 'assetGroup');
                }
            } catch (error) {
                console.error("Error decoding rowClick detail:", error);
            }
        };

        // Attach once
        window.addEventListener("rowClick", handleRowClick);
        // Cleanup on unmount
        return () => {
            window.removeEventListener("rowClick", handleRowClick);
        };
    }, []);



    const handlePrint = () => {
        const contentForPrint = filteredDepreciationContent || depreciationContent;
        const printContent = generateDepreciationReportHtml(
            contentForPrint,
            client,
            date,
            t,
            showEmptyGroups,
            true,
            assetRelations,
            sortConfig
        );
        const win = window.open("", "_blank");
        win.document.write(printContent);
        win.document.close();
        win.focus();
        setTimeout(() => {
            win.print();
        }, 400);
    };

    const handleDownloadPdf = async () => {
        try {
            const contentForPdf = filteredDepreciationContent || depreciationContent;
            const pdfContent = generateDepreciationReportHtml(
                contentForPdf,
                client,
                date,
                t,
                showEmptyGroups,
                true,
                assetRelations,
                sortConfig
            );
            const fileName = `דו"ח פחת - ${client?.name || 'Report'}.pdf`;
            const { blob, fileName: resFileName } = await ReportsService.generatePdfFromHtml(
                pdfContent,
                client?.caseId,
                token,
                {
                    fileName,
                    landscape: true,
                    margins: { Top: 8, Right: 8, Bottom: 8, Left: 8 }
                }
            );
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", resFileName || fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error converting to PDF:", err);
            toast.error(err?.message || "Failed to generate PDF");
        }
    };

    const handleDownloadExcel = () => {
        try {
            const contentForExcel = filteredDepreciationContent || depreciationContent;
            const excelHtml = generateDepreciationReportHtml(
                contentForExcel,
                client,
                date,
                t,
                showEmptyGroups,
                true,
                assetRelations,
                sortConfig
            );
            const blob = new Blob(
                [`\uFEFF${excelHtml}`],
                { type: 'application/vnd.ms-excel;charset=utf-8' }
            );
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `דוח פחת - ${client?.name || 'Report'} - ${formattedTaxYear}.xls`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error generating Excel:', err);
            toast.error(t('FailedToGenerateExcel') || 'Failed to generate Excel');
        }
    };

    const handleSaveSnapshot = async () => {
        if (!depreciationContent?.assetGroups || !reportYear) {
            toast.error(t('NoDataToSave') || 'אין נתונים לשמירה');
            return;
        }

        const items = [];
        depreciationContent.assetGroups.forEach(group => {
            (group.assets || []).forEach(asset => {
                if (asset.asset?.id) {
                    items.push({
                        AssetId: asset.asset.id,
                        DepreciationUntilNow: asset.depreciationUntilNow || 0,
                        DepreciationThisYear: asset.depreciationThisYear || 0,
                    });
                }
            });
        });
        if (items.length === 0) {
            toast.error(t('NoAssetsToSave') || 'אין נכסים לשמירה');
            return;
        }

        const result = await Swal.fire({
            title: t('SaveSnapshotConfirmTitle') || 'שמירת נתוני פחת',
            html: `<p>${t('SaveSnapshotConfirmText') || 'האם לשמור את נתוני הפחת לשנת'} <strong>${reportYear}</strong>?</p>
                   <p style="font-size:13px;color:#666">${items.length} ${t('Assets') || 'נכסים'}</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#304FFF',
            cancelButtonColor: '#999',
            confirmButtonText: t('Save') || 'שמור',
            cancelButtonText: t('Cancel') || 'ביטול',
        });

        if (!result.isConfirmed) return;

        try {
            await AssetService.SaveDepreciationSnapshot(client?.caseId, reportYear, items, token);
            await Swal.fire({
                title: t('SnapshotSaved') || 'נתוני פחת נשמרו',
                html: `<p>${t('SnapshotSavedFor') || 'נתוני הפחת נשמרו בהצלחה לשנת'} <strong>${reportYear}</strong></p>
                       <p style="font-size:13px;color:#666">${items.length} ${t('Assets') || 'נכסים'}</p>`,
                icon: 'success',
                confirmButtonColor: '#304FFF',
                confirmButtonText: t('OK') || 'אישור',
            });
        } catch (err) {
            console.error('Save snapshot error:', err);
            Swal.fire({
                title: t('Error') || 'שגיאה',
                text: t('FailedToSaveSnapshot') || 'שמירת נתוני פחת נכשלה',
                icon: 'error',
                confirmButtonColor: '#d32f2f',
            });
        }
    };

    if (!open) return null;

    return (
        <BoundsWrapper ref={boundsRef}>
            <Draggable
                handle=".report-handle"
                nodeRef={nodeRef}
                bounds="parent"
                position={dragPosition}
                onDrag={(e, data) => setDragPosition({ x: data.x, y: data.y })}
            >
                <ModalOverlay ref={nodeRef}>
                    <div className="report-handle" style={handleStyle}>
                        <button
                            onClick={onClose}
                            style={{
                                background: "transparent",
                                border: "none",
                                fontSize: "16px",
                                cursor: "pointer",
                                color: "#304FFF",
                            }}
                        >
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </button>
                        <div>{t("DepreciationFormYA")}</div>
                        <div />
                    </div>

                    {/* Single row: Search, Date, Checkboxes, Zoom, PDF, Print */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5, pb: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                        <TextField
                            size="small"
                            dir={direction}
                            placeholder={t("Search")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="document-search-bar"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className="document-search-icon" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {!useRange && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CustomYearDatePicker
                                    date={reportDate}
                                    label={t("Year")}   
                                    onDateChange={(newDate) => {
                                        const updated = dayjs(newDate);
                                        setReportDate(updated);
                                        handleEndDateChangeNew?.(updated);
                                    }}
                                    height="36px"
                                />
                            </Box>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useRange}
                                    onChange={(e) => {
                                        setUseRange(e.target.checked);
                                        if (e.target.checked && !rangeEndMonth) {
                                            setRangeEndMonth(new Date());
                                        }
                                    }}
                                    sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' }, p: 0.5 }}
                                    size="small"
                                />
                            }
                            label={<span style={{ fontSize: 12 }}>{t('UpToMonth') || 'עד חודש'}</span>}
                            sx={{ mx: 0 }}
                        />
                        {useRange && (
                            <>
                                <CustomMonthDatePicker
                                    date={rangeEndMonth}
                                    label={t("UpToMonth") || "עד חודש"}
                                    onDateChange={(d) => setRangeEndMonth(d)}
                                    height="36px"
                                />
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                        if (rangeEndMonth && onRefreshWithRange) {
                                            const d = new Date(rangeEndMonth);
                                            const endOfMonth = new Date(Date.UTC(d.getFullYear(), d.getMonth() + 1, 0));
                                            onRefreshWithRange(endOfMonth);
                                        }
                                    }}
                                    disabled={!rangeEndMonth}
                                    sx={{
                                        backgroundColor: '#304FFF', textTransform: 'none', fontSize: 11,
                                        borderRadius: '6px', py: 0.5, minHeight: 32, minWidth: 'auto', px: 1.5,
                                        '&:hover': { backgroundColor: '#2040DD' },
                                    }}
                                >
                                    {t('Refresh') || 'רענן'}
                                </Button>
                            </>
                        )}
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showEmptyGroups}
                                    onChange={(e) => setShowEmptyGroups(e.target.checked)}
                                    sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                />
                            }
                            label={t('ShowEmptyGroups') || 'הצג קבוצות ריקות'}
                            sx={{ fontSize: 13 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={soldOnly}
                                    onChange={(e) => setSoldOnly(e.target.checked)}
                                    sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                />
                            }
                            label={t('SoldOnly') || 'נמכרו בלבד'}
                            sx={{ fontSize: 13 }}
                        />
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                                onClick={handleResetFilters}
                                sx={{ color: '#304FFF', minWidth: 'auto', px: 1, py: 0.5 }}
                            >
                                {t("Reset") || "איפוס"}
                            </Button>
                        )}
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ fontSize: 12, color: '#666' }}>
                                {t('ShowingXOfY') || 'מציג'} {filteredAssetCount} {t('Of') || 'מתוך'} {totalAssetCount}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} sx={{ color: '#304FFF', p: 0.5 }}>
                                    <ZoomOutIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <span style={{ fontSize: 11, minWidth: 32, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                                <IconButton size="small" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))} sx={{ color: '#304FFF', p: 0.5 }}>
                                    <ZoomInIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                onClick={handleDownloadPdf}
                                sx={{ bgcolor: '#304FFF', '&:hover': { bgcolor: '#2639db' }, py: 0.5, minHeight: 32 }}
                            >
                                {t("DownloadPDFFile")}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<TableChartIcon sx={{ fontSize: 16 }} />}
                                onClick={handleDownloadExcel}
                                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, py: 0.5, minHeight: 32 }}
                            >
                                {t("DownloadExcel") || "Excel"}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                                onClick={handlePrint}
                                sx={{ borderColor: '#304FFF', color: '#304FFF', '&:hover': { borderColor: '#2639db', color: '#2639db', bgcolor: 'rgba(48,79,255,0.04)' }, py: 0.5, minHeight: 32 }}
                            >
                                {t("PrintReport")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SaveAltIcon sx={{ fontSize: 16 }} />}
                                onClick={handleSaveSnapshot}
                                sx={{ borderColor: '#2e7d32', color: '#2e7d32', '&:hover': { borderColor: '#1b5e20', bgcolor: 'rgba(46,125,50,0.04)' }, py: 0.5, minHeight: 32 }}
                            >
                                {t("SaveSnapshot") || "שמור נתוני פחת"}
                            </Button>
                        </Box>
                    </Box>

                    <div className="report-body-scroll" style={{ zoom }}>
                        <div dangerouslySetInnerHTML={{ __html: reportHtmlContent }} />
                    </div>
                </ModalOverlay>
            </Draggable>
        </BoundsWrapper>
    );
};

export default AssetDepreciationReportHTML;
